/**
 * BlogPostPage — Individual blog article page.
 * Renders markdown content with SEO meta, JSON-LD, related posts, and CTA.
 */

import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useRef, useEffect } from 'react';
import { useLangStore } from '../../store/langStore';
import { getPostBySlug, getRelatedPosts } from '../../utils/blogLoader';
import { nextLanguageAction } from '../../utils/blogLanguage';
import { SEOHead } from '../../components/blog/SEOHead';
import { BlogHeader } from '../../components/blog/BlogHeader';
import { BlogContent } from '../../components/blog/BlogContent';
import { BlogAnimation } from '../../components/blog/BlogAnimation';
import { RelatedPosts } from '../../components/blog/RelatedPosts';
import { BlogProgressBar } from '../../components/blog/BlogProgressBar';
import { AuthorCard } from '../../components/blog/AuthorCard';
import { ShareButtons } from '../../components/blog/ShareButtons';
import { EmailCapture } from '../../components/blog/EmailCapture';
import { FoundingMemberCTA } from '../../components/blog/FoundingMemberCTA';
import { LessonPreviewCard } from '../../components/blog/LessonPreviewCard';
import { LanguageToggle } from '../../components/ui/LanguageToggle';
import { ArticleLanguageSwitch } from '../../components/blog/ArticleLanguageSwitch';
import { CATEGORY_CONFIG } from '../../types/blog';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { lang, setLang } = useLangStore();
  const navigate = useNavigate();
  // All hooks must be called before any conditional returns (Rules of Hooks)
  const articleRef = useRef<HTMLElement>(null);

  // Resolve the post by slug alone. Slugs are unique per language, so a slug
  // identifies exactly one article regardless of the current UI language.
  const activePost = slug
    ? getPostBySlug(slug, 'no') ?? getPostBySlug(slug, 'en') ?? null
    : null;

  // Show every article in its OWN language and only switch when the reader
  // deliberately asks — never redirect on load. A cold visitor or search
  // crawler landing on an English URL must get the English article, not be
  // bounced to the Norwegian version just because Norwegian is the default.
  // `syncedRef` flips true once the UI language matches the article; only then
  // is a language change treated as a deliberate switch (toggle / switch button).
  const syncedRef = useRef(false);
  useEffect(() => {
    syncedRef.current = false;
  }, [activePost?.slug]);

  useEffect(() => {
    if (!activePost) return;
    const hasCounterpart = !!(
      activePost.translationSlug && getPostBySlug(activePost.translationSlug, lang)
    );
    const action = nextLanguageAction({
      uiLang: lang,
      postLang: activePost.lang,
      synced: syncedRef.current,
      hasCounterpart,
    });
    if (action === 'sync') {
      syncedRef.current = true;
    } else if (action === 'adopt') {
      // Cold load / navigation to a new post → adopt the article's language.
      setLang(activePost.lang);
    } else if (action === 'navigate') {
      // Reader deliberately switched language → jump to the equivalent article.
      navigate(`/undring/${activePost.translationSlug}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, activePost?.slug, activePost?.lang]);

  if (!slug) return <Navigate to="/undring" replace />;

  if (!activePost) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display font-bold text-ink text-3xl mb-4">
            {lang === 'no' ? 'Artikkel ikke funnet' : 'Article not found'}
          </h1>
          <Link
            to="/undring"
            className="text-teal hover:text-teal-dark font-display font-medium underline"
          >
            {lang === 'no' ? '← Tilbake til Undring' : '← Back to Wonder'}
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedPosts(activePost, 3);
  const cat = CATEGORY_CONFIG[activePost.category];

  // Cross-language counterpart (verified to exist) — drives hreflang + the
  // end-of-article language switch.
  const otherLang = activePost.lang === 'no' ? 'en' : 'no';
  const counterpart = activePost.translationSlug
    ? getPostBySlug(activePost.translationSlug, otherLang)
    : null;
  const noSlug = activePost.lang === 'no' ? activePost.slug : activePost.translationSlug;
  const enSlug = activePost.lang === 'en' ? activePost.slug : activePost.translationSlug;
  const alternates =
    counterpart && noSlug && enSlug
      ? [
          { hreflang: 'nb-NO', href: `https://goodatoms.com/undring/${noSlug}` },
          { hreflang: 'en', href: `https://goodatoms.com/undring/${enSlug}` },
          // Norwegian-first brand → Norwegian version is the default
          { hreflang: 'x-default', href: `https://goodatoms.com/undring/${noSlug}` },
        ]
      : undefined;

  return (
    <>
      <SEOHead
        meta={{
          title: activePost.title,
          description: activePost.description,
          url: `https://goodatoms.com/undring/${activePost.slug}`,
          type: 'article',
          locale: activePost.lang === 'no' ? 'nb_NO' : 'en',
          image: activePost.image,
          imageAlt: activePost.imageAlt,
          publishedTime: activePost.date,
          modifiedTime: activePost.updatedAt || activePost.date,
          author: activePost.author,
          section: lang === 'no' ? cat.labelNo : cat.labelEn,
          tags: activePost.tags,
          alternates,
        }}
        post={activePost}
      />

      <BlogProgressBar category={activePost.category} articleRef={articleRef} />

      <div className="min-h-screen bg-page">
        {/* Nav */}
        <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="font-display font-black text-xl tracking-wider text-ink uppercase">
                Good Atoms
              </Link>
              <span className="text-ink-faint font-display text-sm hidden sm:inline">/</span>
              <Link to="/undring" className="font-display text-sm text-ink-muted hover:text-teal transition-colors hidden sm:inline">
                {lang === 'no' ? 'Undring' : 'Wonder'}
              </Link>
            </div>
            <LanguageToggle />
          </div>
        </header>

        {/* Article */}
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <BlogHeader post={activePost} />

          {/* Author attribution — appears just below the header */}
          <AuthorCard lang={lang} />

          {/* Hero animation — the visual centerpiece, shown BEFORE the text */}
          <BlogAnimation
            component={activePost.interactiveComponent}
            category={activePost.category}
            slug={activePost.slug}
            date={activePost.date}
          />

          {/* Hero image fallback (only if no animation exists) */}
          {activePost.image && (
            <div className="max-w-4xl mx-auto mb-10 rounded-2xl overflow-hidden shadow-card">
              <img
                src={activePost.image}
                alt={activePost.imageAlt || activePost.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>
          )}

          <article ref={articleRef}>
            <BlogContent content={activePost.content} category={activePost.category} />
          </article>

          {/* Read this article in the other language (only when a counterpart exists) */}
          {counterpart && <ArticleLanguageSwitch targetLang={otherLang} />}

          {/* Share row — placed BEFORE the FAQ so a reader who skims the body
              can pass the article on before getting drawn into questions. */}
          <ShareButtons
            url={`https://goodatoms.com/undring/${activePost.slug}`}
            title={activePost.title}
            lang={lang}
          />

          {/* FAQ section (rendered from frontmatter for GEO) */}
          {activePost.faq && activePost.faq.length > 0 && (
            <section className="max-w-3xl mx-auto mt-16">
              <h2 className="font-display font-bold text-ink text-2xl mb-6">
                {lang === 'no' ? 'Ofte stilte spørsmål' : 'FAQ'}
              </h2>
              <div className="space-y-4">
                {activePost.faq.map((item, i) => (
                  <details
                    key={i}
                    className="group bg-white rounded-xl border border-gray-100 shadow-sm"
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer font-display font-medium text-ink hover:text-teal transition-colors">
                      {item.question}
                      <span className="ml-4 text-ink-faint group-open:rotate-180 transition-transform">
                        ▾
                      </span>
                    </summary>
                    <div className="px-5 pb-5 font-body text-ink-muted leading-relaxed">
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Product taste — a real lesson preview matched to the post category */}
          <LessonPreviewCard category={activePost.category} lang={lang} />

          {/* Newsletter capture — placed AFTER FAQ, BEFORE the founding-member CTA */}
          <EmailCapture mode="inline" slug={activePost.slug} source="blog" />

          <FoundingMemberCTA lang={lang} />
          <RelatedPosts posts={related} />
        </main>

        {/* Footer */}
        <footer className="border-t border-gray-100 bg-white py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-3">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="font-display font-black text-sm tracking-wider text-ink-muted uppercase">
                Good Atoms
              </Link>
              <p className="font-display text-xs text-ink-faint">
                &copy; {new Date().getFullYear()} Good Atoms AS, Oslo, Norway
              </p>
              <div className="flex gap-4 text-sm font-display text-ink-muted">
                <Link to="/verkstedet" className="hover:text-teal transition-colors">
                  {lang === 'no' ? 'Verkstedet' : 'Studio'}
                </Link>
                <Link to="/privacy" className="hover:text-teal transition-colors">
                  {lang === 'no' ? 'Personvern' : 'Privacy'}
                </Link>
                <Link to="/terms" className="hover:text-teal transition-colors">
                  {lang === 'no' ? 'Vilkår' : 'Terms'}
                </Link>
              </div>
            </div>
            <p className="text-center font-display text-[11px] text-ink-faint max-w-2xl mx-auto">
              {lang === 'no' ? 'Innhold utviklet i ' : 'Content developed in '}
              <Link to="/verkstedet" className="underline hover:text-teal">Verkstedet</Link>
              {lang === 'no'
                ? ' — vårt redaksjonelle lag — med menneskelig fagsjekk før publisering.'
                : ' — our editorial workshop — with human review before publication.'}
            </p>
          </div>
        </footer>

        {/* Sticky email-capture: slides up after 30s or 50% scroll depth.
            Dismissible — remembers dismissal for 7 days. */}
        <EmailCapture mode="sticky-footer" slug={activePost.slug} source="sticky" />
      </div>
    </>
  );
}
